import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const clinicImages = [
  {
    src: 'https://static.readdy.ai/image/e5d7275de0adb13b94926aceb5f8a760/50194b5adcb82ab808a165b7940411d5.png',
    alt: 'clinic.dental_lab',
    label: 'clinic.label_dental',
  },
  {
    src: 'https://static.readdy.ai/image/e5d7275de0adb13b94926aceb5f8a760/f5b971a2cde250b2d93147903277543c.png',
    alt: 'clinic.consultation',
    label: 'clinic.label_consultation',
  },
  {
    src: 'https://static.readdy.ai/image/e5d7275de0adb13b94926aceb5f8a760/90c5239ad2b7fa7a891d779a010a21cf.png',
    alt: 'clinic.waiting',
    label: 'clinic.label_waiting',
  },
  {
    src: 'https://static.readdy.ai/image/e5d7275de0adb13b94926aceb5f8a760/83d00b0f2bc27a764d54b60b760f5e0c.png',
    alt: 'clinic.reception',
    label: 'clinic.label_reception',
  },
];

export default function ClinicGallery() {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  return (
    <section id="clinic" className="py-20 md:py-28 px-6 md:px-10 bg-background-50">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14 md:mb-18">
          <div>
            <span className="text-xs tracking-[0.2em] uppercase text-foreground-400 font-label">
              {t('clinic.tag')}
            </span>
            <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-900 mt-3 leading-tight">
              {t('clinic.heading_line1')}
              <br />
              {t('clinic.heading_line2')}
            </h2>
          </div>
          <p className="text-sm text-foreground-500 max-w-md font-body leading-relaxed">
            {t('clinic.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {/* Large image — dental clinic spans 2 rows on lg */}
          <div
            className="lg:row-span-2 relative group rounded-2xl overflow-hidden cursor-pointer"
            onClick={() => setActiveIndex(0)}
          >
            <div className="h-[280px] md:h-[340px] lg:h-full min-h-[400px] lg:min-h-[640px]">
              <img
                src={clinicImages[0].src}
                alt={t(clinicImages[0].alt)}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="absolute bottom-4 left-4 md:bottom-5 md:left-5 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
              <span className="inline-block px-3 py-1.5 rounded-full bg-white/90 text-foreground-900 text-xs font-label font-semibold">
                {t(clinicImages[0].label)}
              </span>
            </div>
          </div>

          {/* Top-right — consultation */}
          <div
            className="relative group rounded-2xl overflow-hidden cursor-pointer"
            onClick={() => setActiveIndex(1)}
          >
            <div className="h-[200px] md:h-[240px] lg:h-[310px]">
              <img
                src={clinicImages[1].src}
                alt={t(clinicImages[1].alt)}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="absolute bottom-4 left-4 md:bottom-5 md:left-5 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
              <span className="inline-block px-3 py-1.5 rounded-full bg-white/90 text-foreground-900 text-xs font-label font-semibold">
                {t(clinicImages[1].label)}
              </span>
            </div>
          </div>

          {/* Top-right — waiting room */}
          <div
            className="relative group rounded-2xl overflow-hidden cursor-pointer"
            onClick={() => setActiveIndex(2)}
          >
            <div className="h-[200px] md:h-[240px] lg:h-[310px]">
              <img
                src={clinicImages[2].src}
                alt={t(clinicImages[2].alt)}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="absolute bottom-4 left-4 md:bottom-5 md:left-5 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
              <span className="inline-block px-3 py-1.5 rounded-full bg-white/90 text-foreground-900 text-xs font-label font-semibold">
                {t(clinicImages[2].label)}
              </span>
            </div>
          </div>

          {/* Bottom — wide reception, spans 2 cols on lg */}
          <div
            className="lg:col-span-2 relative group rounded-2xl overflow-hidden cursor-pointer"
            onClick={() => setActiveIndex(3)}
          >
            <div className="h-[200px] md:h-[240px] lg:h-[310px]">
              <img
                src={clinicImages[3].src}
                alt={t(clinicImages[3].alt)}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="absolute bottom-4 left-4 md:bottom-5 md:left-5 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
              <span className="inline-block px-3 py-1.5 rounded-full bg-white/90 text-foreground-900 text-xs font-label font-semibold">
                {t(clinicImages[3].label)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {activeIndex !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-3 md:p-6"
          onClick={() => setActiveIndex(null)}
        >
          <div className="relative w-full h-full flex flex-col items-center justify-center" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setActiveIndex(null)}
              className="absolute top-2 right-2 md:top-4 md:right-4 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full transition-colors cursor-pointer z-10"
            >
              <i className="ri-close-line text-xl text-white"></i>
            </button>
            
            {/* Prev / Next arrows */}
            {activeIndex > 0 && (
              <button
                onClick={() => setActiveIndex(activeIndex - 1)}
                className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full transition-colors cursor-pointer z-10"
              >
                <i className="ri-arrow-left-s-line text-xl md:text-2xl text-white"></i>
              </button>
            )}
            {activeIndex < clinicImages.length - 1 && (
              <button
                onClick={() => setActiveIndex(activeIndex + 1)}
                className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full transition-colors cursor-pointer z-10"
              >
                <i className="ri-arrow-right-s-line text-xl md:text-2xl text-white"></i>
              </button>
            )}

            <div className="flex-1 flex items-center justify-center max-w-[95vw] max-h-[92vh]">
              <img
                src={clinicImages[activeIndex].src}
                alt={t(clinicImages[activeIndex].alt)}
                className="max-w-full max-h-[92vh] object-contain rounded-lg"
              />
            </div>
            <p className="text-center text-white/70 text-sm font-label mt-3 pb-2">
              {t(clinicImages[activeIndex].label)} — {activeIndex + 1} / {clinicImages.length}
            </p>
          </div>
        </div>
      )}
    </section>
  );
}